import { styled } from '@mui/material'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface RichTextContentProps {
  content: string
  className?: string
}

export const RichTextContent = ({
  content,
  className,
}: RichTextContentProps) => {
  const isHtml = content.includes('<') && content.includes('>')

  if (isHtml) {
    return (
      <ContentContainer
        className={className}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    )
  }

  return (
    <ContentContainer className={className}>
      <ReactMarkdown
        className="markdown-body rich-text-content"
        linkTarget="_blank"
        remarkPlugins={[remarkGfm]}
        remarkRehypeOptions={{
          handlers: {
            listItem(h, node, parent) {
              const props: {
                [PropertyName: string]:
                  | boolean
                  | number
                  | string
                  | null
                  | undefined
                  | Array<string | number>
              } = {}

              if (typeof node.checked === 'boolean') {
                props.className = ['task-list-item']
                props.dataType = 'taskItem'
                props.dataChecked = node.checked ? 'true' : 'false'
              }

              return h(node, 'li', props, h.all(node))
            },
          },
        }}
        components={{
          // チェックボックス付きリストアイテムのカスタムレンダリング
          li: ({ children, className, ...props }) => {
            if (className?.includes('task-list-item')) {
              const isChecked = (props as any)['data-checked'] === 'true'
              return (
                <TaskListItem>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={true}
                    style={{ marginRight: '8px' }}
                  />
                  {children}
                </TaskListItem>
              )
            }
            return (
              <li className={className} {...props}>
                {children}
              </li>
            )
          },
          // コードブロックのカスタムレンダリング
          code: ({ inline, children, className, ...props }) => {
            if (inline) {
              return (
                <code className={className} {...props}>
                  {children}
                </code>
              )
            }
            return (
              <pre>
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            )
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </ContentContainer>
  )
}

const ContentContainer = styled('div')({
  fontSize: '14px',
  lineHeight: 1.5,
  wordBreak: 'break-word',

  // マークダウンスタイル
  '& .rich-text-content': {
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '16px 0 8px 0',
      fontWeight: 600,
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& ul, & ol': {
      margin: '0 0 8px 0',
      paddingLeft: '20px',
    },
    '& blockquote': {
      margin: '8px 0',
      padding: '0 16px',
      borderLeft: '4px solid #d0d7de',
      color: '#656d76',
    },
    '& code': {
      padding: '2px 4px',
      fontSize: '85%',
      backgroundColor: '#f6f8fa',
      borderRadius: '3px',
      fontFamily:
        'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
    },
    '& pre': {
      padding: '12px',
      margin: '8px 0',
      backgroundColor: '#f6f8fa',
      borderRadius: '6px',
      overflow: 'auto',
      '& code': {
        padding: 0,
        backgroundColor: 'transparent',
        borderRadius: 0,
      },
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '8px 0',
      '& th, & td': {
        border: '1px solid #d0d7de',
        padding: '6px 13px',
      },
      '& th': {
        backgroundColor: '#f6f8fa',
        fontWeight: 600,
      },
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
    '& a': {
      color: '#0969da',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& strong': {
      fontWeight: 600,
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& del': {
      textDecoration: 'line-through',
    },
  },
})

const TaskListItem = styled('li')({
  listStyle: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  marginLeft: '-20px',
  paddingLeft: '20px',
})
